/**
 * Meta (Facebook) App Events Tracking
 * 
 * Hybrid approach:
 * - Client-side: Meta Pixel for PageView / app launch (web context)
 * - Server-side: Conversions API (CAPI) for StartTrial and Subscribe events
 */

import { supabase } from "@/integrations/supabase/client";

const META_PIXEL_ID = import.meta.env.VITE_META_PIXEL_ID;

let pixelInitialized = false;

/**
 * Initialize the Meta Pixel (client-side, for web/webview tracking).
 * On native iOS, install/launch events are handled by the native Meta SDK.
 */
export function initMetaPixel(): void {
  if (pixelInitialized || !META_PIXEL_ID) return;
  pixelInitialized = true;

  try {
    // Inject Meta Pixel base code
    const script = document.createElement("script");
    script.innerHTML = `
      !function(f,b,e,v,n,t,s)
      {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
      n.callMethod.apply(n,arguments):n.queue.push(arguments)};
      if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
      n.queue=[];t=b.createElement(e);t.async=!0;
      t.src=v;s=b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t,s)}(window,document,'script',
      'https://connect.facebook.net/en_US/fbevents.js');
      fbq('init', '${META_PIXEL_ID}');
      fbq('track', 'PageView');
    `;
    document.head.appendChild(script);
    console.log("[Meta] Pixel initialized:", META_PIXEL_ID);
  } catch (e) {
    console.warn("[Meta] Failed to initialize pixel:", e);
  }
}

/**
 * Track a client-side pixel event (for web context).
 */
export function trackPixelEvent(eventName: string, params?: Record<string, unknown>): void {
  try {
    const fbq = (window as any).fbq;
    if (typeof fbq === "function") {
      if (params) {
        fbq("track", eventName, params);
      } else {
        fbq("track", eventName);
      }
      console.log("[Meta] Pixel event:", eventName, params);
    }
  } catch (e) {
    console.warn("[Meta] Pixel event failed:", e);
  }
}

/**
 * Send a server-side event via Meta Conversions API (CAPI).
 * This is the reliable path for attribution — works regardless of ATT status.
 */
export async function trackServerEvent(
  eventName: "StartTrial" | "Subscribe" | "Purchase" | "CompleteRegistration",
  eventData?: Record<string, unknown>,
  accessToken?: string
): Promise<void> {
  try {
    if (!accessToken) {
      const { data } = await supabase.auth.getSession();
      accessToken = data?.session?.access_token ?? undefined;
    }

    if (!accessToken) {
      console.warn("[Meta] No session for CAPI event:", eventName);
      return;
    }

    const { error } = await supabase.functions.invoke("meta-track-event", {
      body: { event_name: eventName, event_data: eventData },
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    if (error) {
      console.error("[Meta] CAPI error:", error);
    } else {
      console.log("[Meta] CAPI event sent:", eventName);
    }
  } catch (e) {
    console.warn("[Meta] CAPI event failed:", e);
  }
}

/**
 * Track trial start — call when the 3-day free trial begins.
 */
export function trackTrialStart(plan?: string, accessToken?: string): void {
  trackPixelEvent("StartTrial", { value: 0, currency: "USD", predicted_ltv: 0, content_name: plan });
  trackServerEvent("StartTrial", { content_name: plan || "premium_trial", trial_period: "3_days" }, accessToken);
}

/**
 * Track subscription purchase — call when user converts to paid.
 */
export function trackSubscriptionPurchase(
  plan: string,
  value: number,
  currency = "USD",
  accessToken?: string
): void {
  const params = { value, currency, content_name: plan };
  trackPixelEvent("Purchase", params);
  trackServerEvent("Subscribe", { ...params, subscription_plan: plan }, accessToken);
}

/**
 * Track app launch (PageView equivalent).
 */
export function trackAppLaunch(): void {
  trackPixelEvent("PageView");
}
